import { element as el } from '../dom.js';
import { AreaBanner } from '../exploration/area-banner.js';
import { BehaviorControls } from '../combat/behavior-controls.js';
import { abilityViews, autocastPatch, refusalVoiceEvent, type AbilityView } from '../combat/ability-views.js';
import { updateWorldStatus } from './world-status.js';
import { InventoryModal, type ItemMenu } from '../inventory/inventory-modal.js';
import { LootPanel, inReach, type LootTarget } from '../supplies/loot-panel.js';
import { currentDrag } from '../supplies/item-slot.js';
import { RestPanel } from '../rest/rest-panel.js';
import { ReadingPanel } from '../readables/reading-panel.js';
import { HeroSelection, inSavedOrder, savedOrderAfter, type SelectionMode } from '../party/hero-selection.js';
import { armCursor } from '../cursors.js';
import type { BehaviorSettings } from '../../../shared/game/behavior-types.js';
import { isFormationShape, type FormationShape } from '../../../shared/game/formation.js';
import type { GameContent } from '../../../shared/game/world-types.js';
import type { ClientMessage, HeroId, Point, WorldSnapshot } from '../../../shared/network/protocol.js';
import type { PingKind } from '../../../shared/network/pings.js';
import { heroSlot, matches } from '../../input/keymap.js';
import type { EmoteKind, PairEmoteKind } from '../../../shared/game/emote-types.js';

const FORMATION_KEY = 'nimblez.formation';
/** The party column's order, by character, as the player last arranged it. */
const PARTY_ORDER_KEY = 'nimblez.party-order';
function savedPartyOrder(): string[] {
  try { const saved: unknown = JSON.parse(localStorage.getItem(PARTY_ORDER_KEY) ?? '[]'); if (Array.isArray(saved)) return saved.filter((key): key is string => typeof key === 'string'); } catch { /* No storage or a damaged entry: the room's order. */ }
  return [];
}
function storedShape(): FormationShape {
  try { const saved = localStorage.getItem(FORMATION_KEY); if (isFormationShape(saved)) return saved; } catch { /* No storage: the line. */ }
  return 'line';
}

/** What the page around the HUD provides: the game, or the scenario viewer. */
export interface WorldHudHost {
  /** Sends a message as is; the HUD itself lets a hero answer the player's orders through `command`. */
  send(message: ClientMessage): void;
  connected(): boolean;
  notice(message: string): void;
  /** Draws the selection in the world. */
  setSelection(ids: HeroId[]): void;
  /** A hero of this player answers an event, if the voice rules allow. */
  speak(heroIds: HeroId[], event: string, snapshot: WorldSnapshot | null, owned: HeroId[]): void;
  /** A hero answers an order the player gave. */
  command(message: ClientMessage, snapshot: WorldSnapshot | null, owned: HeroId[]): void;
  /** True while a page dialog (settings, developer tools) has the keyboard. */
  modalOpen(): boolean;
  /** Page-only updates after each HUD refresh, such as the game's clock and menus. */
  refreshed?(snapshot: WorldSnapshot): void;
}

/**
 * The in-world HUD's state and orders: the selection, powers armed for a target, the weapon choice, and the behavior, pack, loot and
 * rest panels. It turns the player's choices into protocol messages; the server decides everything. The party column, the radial
 * menu and the command rail are drawn by the 3D layer (`application/world-hud-3d.ts`), which reads this state and calls these
 * methods. The game and the scenario viewer use it unchanged, around the markup in `world-hud-markup.ts`.
 */
export class WorldHud {
  readonly selection = new HeroSelection();
  snapshot: WorldSnapshot | null = null;
  /** An ability whose button was pressed and which now waits for the player to click its ally or enemy. */
  private armed: AbilityView | null = null;
  private armedTargets: string[] = [];
  private behaviorOpen = false; private suppliesOpen = false; private restOpen = false;
  /** What the party last heard about a rest vote, and the vote this player was last prompted to answer. */
  private restNoticeSeen: number | null = null; private restPrompted = 0;
  /** The chest, spoils or stall whose window is open next to the pack. */
  private lootTarget: LootTarget | null = null;
  /** A chest or stall a hero is walking to; its window opens once they reach it. */
  private approach: { target: LootTarget; heroId: HeroId; since: number; walked: boolean } | null = null;
  private behaviorControls: BehaviorControls;
  private areaBanner = new AreaBanner(el('area-banner'));
  private restPanel: RestPanel;
  private inventoryPanel: InventoryModal;
  private lootPanel: LootPanel;
  private readingPanel = new ReadingPanel();
  /** For the world scene: chests, stalls, readables and usable props clicked in the world. */
  readonly supplyHandlers = {
    loot: (containerId: string) => this.walkTo({ kind: 'container', id: containerId }),
    merchant: (merchantId: string) => this.walkTo({ kind: 'merchant', id: merchantId }),
    read: (containerId: string) => this.readAt(containerId),
    use: (propId: string) => this.useAt(propId),
  };

  /** The world's content, once a world is entered. */
  content: GameContent | null;
  /** `content` may wait until `enter` (the scenario viewer only knows it once a scenario opens). */
  constructor(private host: WorldHudHost, content?: GameContent) {
    this.content = content ?? null;
    this.behaviorControls = new BehaviorControls((settings, ids) => this.changeBehavior(settings, ids));
    this.restPanel = new RestPanel({
      propose: (kind, choices, wait) => { if (host.connected()) host.send({ type: 'rest-propose', kind, choices, ...wait !== undefined ? { wait } : {} }); },
      vote: (proposalId, accept, choices) => { if (host.connected()) host.send({ type: 'rest-vote', proposalId, accept, choices }); },
      cancel: () => { if (host.connected()) host.send({ type: 'rest-cancel' }); },
    });
    const takeLoot = (heroId: string, containerId: string, pick: { gold?: boolean; itemId?: string }) => { if (host.connected()) this.order({ type: 'loot', heroId, containerId, ...pick }); };
    const buyItem = (heroId: string, merchantId: string, itemId: string) => { if (host.connected()) host.send({ type: 'buy', heroId, merchantId, itemId }); };
    this.inventoryPanel = new InventoryModal({
      use: (heroId, itemId, targetId) => { if (host.connected()) this.order({ type: 'use-item', heroId, itemId, targetId }); },
      buy: buyItem, take: takeLoot, focus: id => this.selectHeroes([id], 'replace', false),
      close: () => { this.lootTarget = null; this.lootPanel.reset(); this.setSuppliesOpen(false); },
      equip: (heroId, slot, itemId) => { if (host.connected()) host.send({ type: 'equip', heroId, slot, itemId }); },
      give: (heroId, itemId, targetId) => { if (host.connected()) host.send({ type: 'give-item', heroId, itemId, targetId }); },
      read: (heroId, itemId) => { if (host.connected()) host.send({ type: 'read', heroId, itemId }); },
    });
    this.lootPanel = new LootPanel({ take: takeLoot, buy: buyItem });
    this.bind();
  }

  /** Puts a hero of this player at place `index` in the party column; selected heroes then fill formation slots in that order. */
  moveHero(id: HeroId, index: number) {
    this.selection.move(id, index); this.refreshSelection();
    try { localStorage.setItem(PARTY_ORDER_KEY, JSON.stringify(savedOrderAfter(this.selection.owned, this.characterKey, savedPartyOrder()))); } catch { /* Private windows and blocked storage keep it for this visit only. */ }
  }
  /** A hero's saved character, which outlives the seat id a room gives it. */
  private characterKey = (id: HeroId) => this.content?.heroes.find(hero => hero.id === id)?.characterId ?? id;
  /** The shape the party keeps when it walks, the same for every move the player gives (the dock's formation column picks it). */
  private shape: FormationShape = storedShape();
  formationShape() { return this.shape; }
  chooseFormation(shape: FormationShape) {
    this.shape = shape;
    try { localStorage.setItem(FORMATION_KEY, shape); } catch { /* Private windows and blocked storage keep it for this visit only. */ }
  }

  // The world scene's callbacks. `turn` is set when the player dragged the order round: the group then faces that way about the clicked point,
  // its slot order running toward the hand the preview drew.
  move(target: Point, turn?: { facing: number; hand: 1 | -1 }) {
    if (this.armed) { this.cancelAbility(); return; }
    if (!this.host.connected()) { this.host.notice('Waiting for the server connection.'); return; }
    if (this.hasSelection()) this.order({ type: 'move', heroIds: this.selection.ids, target, formation: { shape: this.shape, ...turn } });
  }
  attack(targetId: string) {
    if (this.armed) { this.pickAbilityTarget('enemy', targetId); return; }
    // An empty weapon lets each hero strike with the one it prefers, which `chooseWeapon` sets.
    if (this.host.connected() && this.hasSelection()) this.order({ type: 'attack', heroIds: this.selection.ids, targetId, attackId: '' });
  }
  /** `voiced` is false when the code, not the player, picks the hero (walking to a chest, opening a panel). */
  selectHeroes(ids: HeroId[], mode: SelectionMode = 'replace', voiced = true) {
    if (this.armed && ids.length === 1) { this.pickAbilityTarget('ally', ids[0]); return; }
    this.selection.select(ids, mode); this.refreshSelection();
    if (voiced) this.speak(ids.filter(id => this.selection.ids.includes(id)), 'selection');
  }
  /** The powers of the selected heroes this player owns, as the radial menu offers them. */
  abilityViews(): AbilityView[] {
    return this.snapshot && this.content ? abilityViews(this.snapshot, this.content, this.selection.ids.filter(id => this.selection.owned.includes(id))) : [];
  }
  /** The ability waiting for its target, if any. */
  armedAbility() { return this.armed; }
  /** Uses a power at once, or arms it to wait for its target; pressing an armed power again disarms it. */
  activate(view: AbilityView) {
    if (view.disabled) { this.refuse(view); return; }
    if (view.target === 'self') { this.setArmed(null); if (this.host.connected()) this.order({ type: 'ability', heroId: view.heroId, abilityId: view.abilityId }); return; }
    if (this.armed?.heroId === view.heroId && this.armed.abilityId === view.abilityId) this.setArmed(null); else if (this.host.connected()) this.setArmed(view);
  }
  /** A stance notch on a medallion pressed: that hero's stance switches as its menu button would, saying why when it cannot. */
  toggleStance(heroId: HeroId, abilityId: string) {
    if (!this.snapshot || !this.content || !this.selection.owned.includes(heroId)) return;
    const view = abilityViews(this.snapshot, this.content, [heroId]).find(view => view.abilityId === abilityId);
    if (view) this.activate(view);
  }
  /** A refused press: the hero says why, when there is a line for it. */
  refuse(view: AbilityView) { const event = refusalVoiceEvent(view.disabled); if (event) this.speak([view.heroId], event); }
  toggleAutocast(view: AbilityView) {
    const hero = this.snapshot?.heroes.find(candidate => candidate.id === view.heroId);
    if (this.host.connected() && hero) this.host.send({ type: 'behavior', heroIds: [view.heroId], settings: autocastPatch(view, hero.behavior) });
  }
  /** Cancels the selected heroes' orders. */
  stop() { if (this.host.connected() && this.hasSelection()) this.order({ type: 'stop', heroIds: this.selection.ids }); }
  /** Uses a power on a target chosen already (from the radial menu), without arming it. */
  useOn(view: AbilityView, targetId: string) {
    if (view.disabled) { this.refuse(view); return; }
    if (!this.host.connected()) return;
    this.setArmed(null);
    this.order({ type: 'ability', heroId: view.heroId, abilityId: view.abilityId, ...view.target === 'self' ? {} : { targetId } });
  }
  /**
   * Arms a power that takes several targets with its first one already chosen (from the radial menu): the next enemy clicked joins
   * it, and clicking the first again uses it on that one alone.
   */
  armWith(view: AbilityView, firstTarget: string) {
    if (view.disabled) { this.refuse(view); return; }
    if (!this.host.connected()) return;
    this.setArmed(view); this.armedTargets = [firstTarget];
  }
  /** The targets already chosen for the armed power. */
  armedTargetIds(): readonly string[] { return this.armedTargets; }
  /** Orders these heroes to attack a target with their chosen weapon. */
  attackWith(heroIds: HeroId[], targetId: string) {
    if (this.host.connected()) this.order({ type: 'attack', heroIds, targetId, attackId: '' });
  }
  /** The selected heroes (but the ally itself) follow a party hero until given another order. */
  follow(targetId: HeroId) {
    const heroIds = this.selection.ids.filter(id => this.selection.owned.includes(id) && id !== targetId);
    if (this.host.connected() && heroIds.length) this.order({ type: 'follow', heroIds, targetId });
  }
  /** The heroes gesture for the room; a pair kind has the one hero meet `targetId`. The server says why when it cannot (in a fight, busy). */
  emote(kind: EmoteKind | PairEmoteKind, heroIds: HeroId[], at?: Point, targetId?: string) {
    if (this.host.connected() && heroIds.length) this.order({ type: 'emote', heroIds, kind, ...at ? { at: { x: at.x, z: at.z } } : {}, ...targetId ? { targetId } : {} });
  }
  stopHeroes(heroIds: HeroId[]) { if (this.host.connected()) this.order({ type: 'stop', heroIds }); }
  /** Opens one hero's pack. */
  openPackFor(id: HeroId) { this.selectHeroes([id], 'replace', false); this.setSuppliesOpen(true); }
  enemyTargetAllowed() { return this.host.connected() && (!this.armed || this.armed.target === 'enemy' || this.armed.target === 'creature'); }
  allyFigureTargetAllowed(id: string) {
    if (!this.host.connected()) return false;
    if (!this.armed) return this.selection.owned.includes(id);
    // Breath of Life reaches only the Dying, Lay on Hands anyone; every other ally power only allies who can act.
    const self = id === this.armed.heroId && (!!this.armed.dyingTarget || !!this.armed.healTarget);
    return (this.armed.target === 'ally' || this.armed.target === 'creature') && (id !== this.armed.heroId || self) && !!this.snapshot?.heroes.some(hero => hero.id === id && !hero.health.dead && (this.armed!.healTarget || hero.health.dying === !!this.armed!.dyingTarget));
  }

  /** The party is entering a world: `ids` are this player's heroes. Call `heroesChanged` once the world is drawn. */
  enter(ids: HeroId[], content: GameContent) {
    this.content = content; this.selection.reset(inSavedOrder(ids, this.characterKey, savedPartyOrder())); this.snapshot = null; this.areaBanner.reset(); this.readingPanel.reset();
  }
  /** On entering, and when another player's heroes arrive or change. */
  heroesChanged() { this.refreshSelection(); }
  receive(snapshot: WorldSnapshot) { this.snapshot = snapshot; this.refresh(); }
  /** Back out of the world: panels closed, nothing armed or selected. */
  reset() {
    this.behaviorOpen = false; this.suppliesOpen = false; this.restOpen = false; this.lootTarget = null; this.approach = null; this.restNoticeSeen = null; this.restPrompted = 0;
    this.armed = null; this.armedTargets = []; document.body.classList.remove('targeting'); armCursor(null);
    this.selection.reset([]); this.snapshot = null; this.areaBanner.reset(); this.readingPanel.reset(); this.inventoryPanel.setOpen(false);
  }
  pause() { if (this.snapshot && this.host.connected()) this.host.send({ type: 'pause', paused: !this.snapshot.paused }); }
  /** Points a spot, or the unit on it, out to everyone in the room. Not an order: no hero answers it. */
  ping(kind: PingKind, at: Point, targetId?: string) { if (this.host.connected()) this.host.send({ type: 'map-ping', kind, at: { x: at.x, z: at.z }, ...targetId ? { targetId } : {} }); }
  /** Which of the HUD's panels are open, for the command rail. */
  panels() { return { behavior: !el('behavior-panel').hidden, pack: !el('supplies-panel').hidden, rest: !el('rest-panel').hidden }; }
  /**
   * Automatic (`''`) and the weapons every selected hero carries; the chosen one is the weapon they all prefer, or Automatic when
   * they prefer none or differ.
   */
  weaponChoices(): { id: string; name: string; chosen: boolean }[] {
    if (!this.content || !this.selection.ids.length) return [];
    const heroes = this.content.heroes.filter(hero => this.selection.ids.includes(hero.id));
    const preferred = new Set(this.selection.ids.map(id => this.snapshot?.heroes.find(hero => hero.id === id)?.behavior.preferredAttackId ?? ''));
    const chosen = preferred.size === 1 ? [...preferred][0] : '';
    const shared = (heroes[0]?.profile.attacks ?? []).filter(attack => heroes.every(hero => hero.profile.attacks.some(item => item.id === attack.id)));
    return [{ id: '', name: 'Automatic weapon', chosen: !shared.some(attack => attack.id === chosen) },
      ...shared.map(attack => ({ id: attack.id, name: attack.name, chosen: attack.id === chosen }))];
  }
  /** Picks the weapon the selected heroes prefer (`''` for Automatic). */
  chooseWeapon(id: string) { this.changeBehavior({ preferredAttackId: id || null }); }
  /** Whether the item being dragged can be used on this hero (a potion for its carrier, or an ally in reach who needs it). */
  /** Who draws an item's actions round its slot in the inventory (the 3D HUD). */
  setItemMenu(menu: ItemMenu) { this.inventoryPanel.setItemMenu(menu); }
  canDropOn(heroId: HeroId) { const drag = currentDrag(); return !!drag && this.inventoryPanel.canUse(drag, heroId, this.snapshot); }
  /** Uses the dragged item on this hero; returns whether it was taken. */
  dropOn(heroId: HeroId) {
    const drag = currentDrag();
    if (!drag || !this.inventoryPanel.canUse(drag, heroId, this.snapshot)) return false;
    document.body.classList.remove('dragging-item'); this.inventoryPanel.drop(drag, heroId); return true;
  }

  /** The HUD's keys. Returns true when it handled the key, so the page should not act on it too. */
  key(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement, hidden = el('hud').hidden;
    const textEntry = target.closest('input[type="text"], input[type="search"], input[type="email"], input[type="password"], input[type="url"], input[type="tel"], textarea, [contenteditable="true"]');
    if (matches(event, 'pause') && !hidden && !textEntry) {
      event.preventDefault();
      if (!event.repeat && !this.host.modalOpen()) this.pause();
      return true;
    }
    if (matches(event, 'cancel') && this.readingPanel.open) { this.readingPanel.close(); return true; }
    if (this.suppliesOpen && matches(event, 'pack') && !event.repeat && !event.ctrlKey && !event.metaKey && !target.closest('input, textarea, select')) { event.preventDefault(); this.setSuppliesOpen(false); return true; }
    if (this.suppliesOpen && el<HTMLDialogElement>('supplies-panel').open) return true;
    if (matches(event, 'cancel') && this.armed) { this.cancelAbility(); return true; }
    if (matches(event, 'cancel') && this.lootTarget) { this.closeLoot(); return true; }
    if (matches(event, 'cancel') && !event.repeat && !hidden && !this.host.modalOpen() && !target.closest('input, textarea, select')) {
      if (this.behaviorOpen && !el('behavior-panel').hidden) { this.setBehaviorOpen(false); return true; }
      if (this.restOpen && !el('rest-panel').hidden) { this.setRestOpen(false); return true; }
      if (this.selection.ids.length) { this.selectHeroes([], 'replace', false); return true; }
    }
    if (event.repeat || this.host.modalOpen() || hidden || target.closest('input, button, textarea, select, summary')) return false;
    if (matches(event, 'behavior') && !event.ctrlKey && !event.metaKey) { event.preventDefault(); this.toggleBehavior(); return true; }
    if (matches(event, 'pack') && !event.ctrlKey && !event.metaKey) { event.preventDefault(); this.toggleSupplies(); return true; }
    // 1–9 pick the first nine heroes in portrait order and 0 the tenth; Shift adds or removes.
    const slot = heroSlot(event);
    if (slot >= 0) { const id = this.selection.owned[slot]; if (id) this.selectHeroes([id], event.shiftKey ? 'toggle' : 'replace'); return true; }
    if (matches(event, 'run') && !event.ctrlKey && !event.metaKey) { event.preventDefault(); this.toggleRun(); return true; }
    if (matches(event, 'selectAll') && !event.ctrlKey && !event.metaKey) { event.preventDefault(); this.selectHeroes(this.selection.owned); return true; }
    return false;
  }

  /** R: the selected heroes all run, or all walk if every one already runs, so a mixed selection lines up first. */
  private toggleRun() {
    if (!this.snapshot || !this.host.connected() || !this.hasSelection()) return;
    const heroes = this.snapshot.heroes.filter(hero => this.selection.ids.includes(hero.id) && !hero.health.dead);
    const start = heroes.some(hero => !hero.combat.running);
    for (const hero of heroes) if ((hero.combat.running === true) !== start) this.order({ type: 'ability', heroId: hero.id, abilityId: 'run' });
  }
  private speak(heroIds: HeroId[], event: string) { this.host.speak(heroIds, event, this.snapshot, this.selection.owned); }
  /** Shift is held: moves, attacks and powers join the end of each hero's queue instead of replacing its orders. */
  private queueing = false;
  /** Sends a command the player gave, and lets a hero answer it. */
  private order(message: ClientMessage) {
    if (this.queueing && (message.type === 'move' || message.type === 'attack' || message.type === 'ability')) message = { ...message, queue: true };
    // Any other order for the hero on the way to a chest or stall calls the visit off.
    if (this.approach && message.type !== 'open-container' && ('heroIds' in message ? message.heroIds ?? [] : 'heroId' in message ? [message.heroId] : []).includes(this.approach.heroId)) this.approach = null;
    this.host.send(message); this.host.command(message, this.snapshot, this.selection.owned); for (const listener of this.orderListeners) listener(message); }
  private orderListeners: ((message: ClientMessage) => void)[] = [];
  /** Called after each order the player gives (move, attack, power, item, stop). */
  onOrder(listener: (message: ClientMessage) => void) { this.orderListeners.push(listener); }
  private hasSelection() { if (this.selection.ids.length) return true; this.host.notice('Select a hero or use Select all.'); return false; }
  private changeBehavior(settings: Partial<BehaviorSettings>, ids = this.selection.ids) { if (this.host.connected() && this.hasSelection()) this.host.send({ type: 'behavior', heroIds: ids, settings }); }
  private get world(): GameContent { if (!this.content) throw new Error('The HUD has no world yet.'); return this.content; }

  private setArmed(view: AbilityView | null) {
    this.armed = view; this.armedTargets = []; document.body.classList.toggle('targeting', !!view); armCursor(view?.abilityId ?? null);
    this.refresh();
  }
  private cancelAbility() { if (this.armed) this.setArmed(null); }
  private pickAbilityTarget(kind: AbilityView['target'], targetId: string) {
    const armed = this.armed;
    if (!armed || armed.target !== kind && armed.target !== 'creature') { this.host.notice(armed ? `${armed.name} needs ${armed.target === 'ally' ? 'an ally' : 'an enemy'}.` : ''); return; }
    // Only a Dying caster may target itself, with a power that needs a Dying target (Breath of Life); a healing touch reaches the caster too.
    const selfRevive = armed.healTarget || !!armed.dyingTarget && !!this.snapshot?.heroes.some(hero => hero.id === armed.heroId && hero.health.dying);
    if (kind === 'ally' && targetId === armed.heroId && !selfRevive) { this.host.notice('Choose a different party member.'); return; }
    if (armed.maxTargets && armed.maxTargets > 1) {
      if (!this.armedTargets.length) {
        this.armedTargets = [targetId];
        return;
      }
      if (this.host.connected()) this.order({ type: 'ability', heroId: armed.heroId, abilityId: armed.abilityId,
        targetIds: targetId === this.armedTargets[0] ? this.armedTargets : [...this.armedTargets, targetId] });
      this.setArmed(null); return;
    }
    if (this.host.connected()) this.order({ type: 'ability', heroId: armed.heroId, abilityId: armed.abilityId, targetId });
    this.setArmed(null);
  }

  private setBehaviorOpen(open: boolean) { this.behaviorOpen = open; if (open) { this.suppliesOpen = false; this.restOpen = false; } this.refreshPanels(); }
  private setSuppliesOpen(open: boolean) { this.suppliesOpen = open; if (open) { this.behaviorOpen = false; this.restOpen = false; } this.refreshPanels(); }
  private setRestOpen(open: boolean) { this.restOpen = open; if (open) { this.behaviorOpen = false; this.suppliesOpen = false; } this.refreshPanels(); }
  private refreshPanels() { this.refreshBehaviorPanel(); this.refreshSuppliesPanel(); this.refreshRestPanel(); }
  private refreshRestPanel() {
    // The rest panel does not need a selection: a vote called by someone else must be answerable by every player.
    el('rest-panel').hidden = !this.restOpen || !this.selection.owned.length || el('hud').hidden;
    if (!el('rest-panel').hidden && this.snapshot) this.restPanel.update(this.snapshot, this.world, this.selection.owned);
  }
  toggleRest() { if (el('hud').hidden) return; this.setRestOpen(!(this.restOpen && !el('rest-panel').hidden)); }
  private actingHero() { return this.selection.ids.find(id => this.selection.owned.includes(id)); }
  private refreshSuppliesPanel() {
    this.checkApproach();
    this.inventoryPanel.setOpen(this.suppliesOpen && !!this.actingHero() && !el('hud').hidden);
    if (!el('supplies-panel').hidden && this.snapshot) this.inventoryPanel.update(this.snapshot, this.world, this.actingHero(), this.selection.owned);
    if (this.lootTarget && this.snapshot && !el('hud').hidden && !this.lootPanel.update(this.snapshot, this.world, this.actingHero(), this.lootTarget)) this.lootTarget = null;
    el('loot-panel').hidden = !this.lootTarget || el('hud').hidden;
  }
  private closeLoot() { this.lootTarget = null; this.lootPanel.reset(); this.refreshSuppliesPanel(); }
  private openLoot(target: LootTarget, hero: string) {
    this.lootTarget = target; this.lootPanel.reset(); this.selectHeroes([hero], 'replace', false); this.setSuppliesOpen(true);
  }
  toggleSupplies() { if (el('hud').hidden) return; if (this.suppliesOpen && !el('supplies-panel').hidden) this.setSuppliesOpen(false); else if (this.hasSelection()) this.setSuppliesOpen(true); }
  private lootSpot(target: LootTarget) {
    return target.kind === 'container' ? this.snapshot?.containers.find(candidate => candidate.id === target.id) : this.world.merchants?.find(candidate => candidate.id === target.id);
  }
  /** Walks the nearest selected hero to a chest or stall; its window opens once they are in reach. */
  private walkTo(target: LootTarget) {
    const spot = this.lootSpot(target), hero = spot && this.nearestSelected(spot);
    if (!this.host.connected()) { this.host.notice('Waiting for the server connection.'); return; }
    if (!spot || !hero) { this.host.notice(target.kind === 'merchant' ? 'Select a hero to visit the merchant.' : 'Select a hero to collect that.'); return; }
    if (target.kind === 'container') this.order({ type: 'open-container', heroId: hero.id, containerId: target.id });
    else this.speak([hero.id], 'interact');
    if (inReach(hero, spot, target.kind)) { this.approach = null; this.openLoot(target, hero.id); return; }
    // Stand just in front of the stall, on the side the hero approaches from.
    const dx = hero.x - spot.x, dz = hero.z - spot.z, length = Math.hypot(dx, dz) || 1;
    if (target.kind === 'merchant') this.host.send({ type: 'move', heroIds: [hero.id], target: { x: spot.x + dx / length * 2.2, z: spot.z + dz / length * 2.2 } });
    this.approach = { target, heroId: hero.id, since: performance.now(), walked: false };
  }
  /** Opens the window when the walking hero arrives; drops the visit if they die or stop short. */
  private checkApproach() {
    const approach = this.approach, hero = approach && this.snapshot?.heroes.find(candidate => candidate.id === approach.heroId), spot = approach && this.lootSpot(approach.target);
    if (!approach) return;
    if (!hero || !spot || hero.health.dead || this.snapshot!.inCombat) { this.approach = null; return; }
    if (inReach(hero, spot, approach.target.kind)) { this.approach = null; this.openLoot(approach.target, hero.id); return; }
    // The server plans the walk a tick or two after the order, so a hero standing still at first has not yet set off.
    if (hero.route.length) approach.walked = true;
    else if (approach.walked || performance.now() - approach.since > 1500) this.approach = null;
  }
  /** Walks the nearest selected hero to a readable lying in the world; the page opens when the server reports the reading. */
  private readAt(containerId: string) {
    const spot = this.snapshot?.containers.find(candidate => candidate.id === containerId), hero = spot && this.nearestSelected(spot);
    if (!this.host.connected()) { this.host.notice('Waiting for the server connection.'); return; }
    if (!spot || !hero) { this.host.notice('Select a hero to read that.'); return; }
    this.speak([hero.id], 'interact');
    this.order({ type: 'read', heroId: hero.id, containerId });
  }
  /** Walks the nearest selected hero to a usable prop, which they use on arrival. */
  private useAt(propId: string) {
    const prop = this.world.usableProps?.find(candidate => candidate.id === propId), hero = prop && this.nearestSelected(prop);
    if (!this.host.connected()) { this.host.notice('Waiting for the server connection.'); return; }
    if (!prop || !hero) { this.host.notice('Select a hero to use that.'); return; }
    this.speak([hero.id], 'interact');
    this.order({ type: 'use-prop', heroId: hero.id, propId });
  }
  /** The selected hero closest to a point; supply interactions are made by one hero at a time. */
  private nearestSelected(point: Point) {
    return (this.snapshot?.heroes ?? []).filter(hero => this.selection.ids.includes(hero.id) && !hero.health.dead)
      .sort((a, b) => Math.hypot(a.x - point.x, a.z - point.z) - Math.hypot(b.x - point.x, b.z - point.z))[0];
  }
  private refreshBehaviorPanel() { el('behavior-panel').hidden = !this.behaviorOpen || !this.selection.ids.length || el('hud').hidden; this.refreshBehaviorControls(); }
  /** The panel's switches follow every snapshot only while it is open; opening it brings them up to date. */
  private refreshBehaviorControls() {
    if (!this.snapshot || el('behavior-panel').hidden) return;
    this.behaviorControls.update(this.snapshot.heroes.filter(hero => this.selection.ids.includes(hero.id)));
  }
  openBehaviorFor(id: HeroId) { this.selectHeroes([id], 'replace', false); this.setBehaviorOpen(true); }
  toggleBehavior() { if (el('hud').hidden) return; if (this.behaviorOpen && !el('behavior-panel').hidden) this.setBehaviorOpen(false); else if (this.hasSelection()) this.setBehaviorOpen(true); }

  /** Redraws the HUD from the latest snapshot. */
  refresh() {
    const snapshot = this.snapshot;
    if (!snapshot || !this.content) return;
    updateWorldStatus(snapshot, this.selection.ids);
    this.refreshSuppliesPanel();
    // Tell everyone how a vote ended, but not what happened before this client joined.
    const outcome = snapshot.restNotice;
    if (this.restNoticeSeen !== null && outcome && outcome.id !== this.restNoticeSeen) this.host.notice(outcome.text);
    this.restNoticeSeen = outcome?.id ?? 0;
    // A vote another player called opens the panel so it can be answered.
    const prompt = this.restPanel.pendingFor(snapshot, this.selection.owned);
    if (prompt && prompt !== this.restPrompted) { this.restPrompted = prompt; this.setRestOpen(true); }
    this.refreshRestPanel();
    const views = this.abilityViews();
    // An armed ability stays armed only while its hero is still selected and able to use it.
    const armed = this.armed;
    if (armed && !views.some(view => view.heroId === armed.heroId && view.abilityId === armed.abilityId && !view.disabled)) { this.armed = null; this.armedTargets = []; document.body.classList.remove('targeting'); }
    this.refreshBehaviorControls();
    this.areaBanner.update(this.world.map.landmarks ?? [], snapshot.heroes.filter(hero => !hero.health.dead).map(hero => ({ x: hero.x, z: hero.z })));
    this.readingPanel.receive(snapshot, this.world, this.selection.owned);
    this.host.refreshed?.(snapshot);
    for (const listener of this.listeners) listener();
  }
  private listeners: (() => void)[] = [];
  /** Called after every refresh, for views drawn from the HUD's state (the 3D layer). */
  onRefresh(listener: () => void) { this.listeners.push(listener); }
  refreshSelection() {
    this.host.setSelection(this.selection.ids);
    if (!this.content) return;
    this.behaviorControls.mount(this.world.heroes.filter(hero => this.snapshot?.heroes.some(state => state.id === hero.id) || this.selection.owned.includes(hero.id)), this.selection.ids);
    this.refreshBehaviorPanel(); this.refreshSuppliesPanel();
    this.refresh();
  }

  private bind() {
    el('supplies-close').addEventListener('click', () => this.setSuppliesOpen(false));
    el('loot-close').addEventListener('click', () => this.closeLoot());
    el('rest-close').addEventListener('click', () => this.setRestOpen(false));
    el('behavior-close').addEventListener('click', () => this.setBehaviorOpen(false));
    // Pointer and key events both carry Shift, so a click on the world, the HUD or a hotkey all see the same state.
    const shift = (event: KeyboardEvent | PointerEvent) => { this.queueing = event.shiftKey; };
    addEventListener('keydown', shift, true); addEventListener('keyup', shift, true); addEventListener('pointerdown', shift, true); addEventListener('pointerup', shift, true);
    addEventListener('blur', () => { this.queueing = false; });
  }
}
